import * as turf from "@turf/turf";
import mapboxgl from "mapbox-gl";
import {
  getElevationAtDistanceKm,
  exponentialSmoothingAlpha,
  smoothLngLat,
  cappedCentre,
  metersPerPixel,
  advanceProgress,
  easeInOutCubic,
  interpolateCamera,
  lerpAngleShortest
} from "./trailPreviewMath";
import { resolveFlyoverLineStyle } from "./flyoverLineStyle";
import {
  buildFlyoverRoute,
  coordAtKm,
  lookAheadCoordAtKm,
  drawnStrokesUpToKm
} from "./flyoverRoute";

// Relive-style flyover: the route line draws at a CONSTANT speed while the camera follows the head at a
// fixed zoom, slowly orbiting; a flat-2D -> 3D intro plays first and a 2D zoom-out plays at the end.
// Speed is ms per km of trail (lower = faster). The mobile apps use ~2.2 s/km at 1x.
const SPEED_CONFIG = {
  slow: { durationPerKm: 4400 },
  medium: { durationPerKm: 2200 },
  fast: { durationPerKm: 1100 },
  ultrafast: { durationPerKm: 700 },
  hyperspeed: { durationPerKm: 400 }
};

const FLYOVER_ZOOM = 14;     // constant follow zoom
const FLYOVER_PITCH = 45;    // top-down-ish tilt (route reads clearly, incl. mountains)
const ORBIT_DEGREES = 180;   // total slow orbit over the whole flyover
const INTRO_MS = 2000;       // flat-2D -> 3D tilt
const OUTRO_MS = 2500;       // zoom-out to the whole route in 2D
// Time constant (s) for easing the camera centre toward the head. The head moves piecewise-linearly
// along the route, so its pan direction snaps at every vertex; smoothing the centre rounds those
// corners (the marker + drawn line stay on the true head). Larger = smoother but more head lag.
const CENTER_SMOOTH_TAU = 0.35;
const MAX_FRAME_SECONDS = 0.1; // clamp dt so a tab stall can't make the centre lurch on resume
// How far the eased centre may trail the head, as a fraction of half the viewport's smaller side.
// Without it the centre eases the WHOLE way after a head that stepped over a gap between two of a
// hike's routes, which reads as the camera flying to the start of the next route. Same value the
// Android flyover uses (HikePreviewCoordinator.maxHeadOffsetFraction).
const MAX_HEAD_OFFSET_FRACTION = 0.3;

// Relive-style chase camera (render mode only): fly low behind the head, looking forward along the
// route, heading tracking the direction of travel. Used for the shareable video; the live in-app
// flyover keeps the orbit/framing camera.
const CHASE_PITCH = 60;                 // tilt -> horizon visible, but head stays comfortably in frame
const CHASE_ZOOM = 14.8;                // close follow
const CHASE_BEARING_LOOKAHEAD_KM = 0.12; // window used to derive the travel direction
const CHASE_BEARING_SMOOTH_TAU = 0.7;   // heading easing time constant (s); higher = smoother, laggier

/**
 * Relive-style trail flyover. Returns a controller:
 * pause(), resume(), stop(), isPaused(), fastForward(), rewind(), getProgress(),
 * getEndState(), getStartState(), setSpeedMultiplier(), getSpeedMultiplier(), then(), destroy().
 */
export function previewTrail(
  map,
  trailCoords,                 // one continuous track, OR a hike's route pieces as [[coord,...],...]
  mode = "medium",
  styleOptions = {},           // route line + head marker paint; see flyoverLineStyle.ts
  progressCallback,
  cameraOptions = {}
) {
  const config = SPEED_CONFIG[mode] || SPEED_CONFIG.medium;
  const style = resolveFlyoverLineStyle(styleOptions);

  // A hike's pieces are stitched into the walk first — oriented to follow one another, run together
  // where they meet, and broken where they genuinely don't (see flyoverRoute.ts). A single track
  // (a recording) comes back as one stroke, unreordered and unflipped.
  const route = buildFlyoverRoute(trailCoords);
  const coords3d = route.coords;
  const coords2d = coords3d.map(c => [c[0], c[1]]);
  // Gaps are not walked, so they are not in the distance either: this is the length the marker
  // travels and the number the readout shows.
  const totalKm = route.totalKm;
  const cumulativeDistKm = route.cumulativeKm;
  const startCoord = coords2d[0];

  // Fixed orbit-base bearing: start -> farthest point (robust for loops; captures the main extent).
  let far = startCoord;
  let farthest = 0;
  for (const c of coords2d) {
    const d = turf.distance(turf.point(startCoord), turf.point(c), { units: "kilometers" });
    if (d > farthest) { farthest = d; far = c; }
  }
  const routeBearing = farthest > 0.02 ? turf.bearing(turf.point(startCoord), turf.point(far)) : 0;

  // Progress line layer (blue, draws behind the head). Bordered with its own colour at half
  // brightness — the same recipe both apps use for every route line — so it holds up against bright
  // satellite imagery instead of dissolving into it.
  if (!map.getSource("progressTrail")) {
    map.addSource("progressTrail", { type: "geojson", data: turf.lineString([startCoord, startCoord]) });
  }
  // Each layer is checked on its own, not behind the source's existence: a caller that removes one of
  // the pair leaves the source behind (GL JS refuses to drop a source a layer still uses), and a
  // source-only guard would then never restore the missing half. The casing goes in BELOW the line
  // whichever order they are (re)created in.
  if (!map.getLayer("progressTrailBorder")) {
    // The border is a wider casing line under the route rather than `line-border-width`: GL JS accepts
    // that paint property (it's what the native SDKs use) but draws nothing for it.
    map.addLayer({
      id: "progressTrailBorder",
      type: "line",
      source: "progressTrail",
      layout: { "line-join": "round", "line-cap": "round" },
      paint: { "line-color": style.lineBorderColor, "line-width": style.lineCasingWidth }
    }, map.getLayer("progressTrail") ? "progressTrail" : undefined);
  }
  if (!map.getLayer("progressTrail")) {
    map.addLayer({
      id: "progressTrail",
      type: "line",
      source: "progressTrail",
      layout: { "line-join": "round", "line-cap": "round" },
      paint: { "line-color": style.lineColor, "line-width": style.lineWidth }
    });
  }

  // Head indicator drawn as a CIRCLE LAYER (teal dot + white ring), not a DOM Marker. A DOM marker is
  // projected at sea level and visibly floats above 3D terrain at high pitch (the chase camera); a
  // circle layer's anchor is elevated to the terrain surface, so the puck stays glued to the route
  // like the draped progress line. It also can't be hidden by occlusion fading or a missing asset.
  if (!map.getSource("flyoverHead")) {
    map.addSource("flyoverHead", { type: "geojson", data: turf.point(startCoord) });
    // Dark disc UNDER the puck — the marker's counterpart of the line border, so the white ring keeps
    // its contrast over pale rock/snow as well as forest. Same source, so it follows the head.
    map.addLayer({
      id: "flyoverHeadBorder",
      type: "circle",
      source: "flyoverHead",
      paint: {
        "circle-radius": style.headBorderRadius,
        "circle-color": style.headBorderColor,
        "circle-pitch-alignment": "viewport"
      }
    });
    map.addLayer({
      id: "flyoverHead",
      type: "circle",
      source: "flyoverHead",
      paint: {
        "circle-radius": style.headRadius,
        "circle-color": style.headColor,
        "circle-stroke-color": style.headRing,
        "circle-stroke-width": style.headRingWidth,
        "circle-pitch-alignment": "viewport" // screen-facing dot at the terrain anchor
      }
    });
  }

  return animateReliveFlyover({
    map,
    route,
    coords3d,
    cumulativeDistKm,
    totalKm,
    routeBearing,
    duration: totalKm * config.durationPerKm,
    progressCallback,
    renderMode: cameraOptions.renderMode === true,
    chaseCam: cameraOptions.chaseCam === true
  });
}

// ----------------------------- Animation -----------------------------

function animateReliveFlyover(opts) {
  const { map, route, coords3d, cumulativeDistKm, totalKm, routeBearing, duration, progressCallback } = opts;
  const setHead = (coord) => {
    const src = map.getSource("flyoverHead");
    if (src) src.setData(turf.point(coord));
  };
  // renderMode: deterministic, externally-driven frame stepping for headless video capture.
  // When true we skip the rAF loop and the easeTo intro/outro (neither is steppable frame-by-frame);
  // the caller advances frames via renderFlyoverFrame(). Defaults false so the live path is unchanged.
  const renderMode = opts.renderMode === true;
  // Chase camera (render mode): fly forward behind the head instead of orbiting. Defaults off so the
  // live flyover is unchanged.
  const chaseCam = opts.chaseCam === true;
  const startCoord = coords3d.length > 0 ? [coords3d[0][0], coords3d[0][1]] : [0, 0];

  // Travel direction at the very start, for the chase-cam intro hand-off (look-ahead from start).
  const initialAhead = lookAheadCoordAtKm(route, 0, CHASE_BEARING_LOOKAHEAD_KM) || startCoord;
  const initialTravelBearing = totalKm > 0 ? turf.bearing(turf.point(startCoord), turf.point(initialAhead)) : routeBearing;

  // Summit (high point) progress fraction, for the hero-beat slow-down.
  let summitT = 0.5;
  { let mi = 0; for (let i = 1; i < coords3d.length; i++) if (coords3d[i][2] > coords3d[mi][2]) mi = i; summitT = totalKm > 0 ? cumulativeDistKm[mi] / totalKm : 0.5; }
  // Cinematic speed envelope (render mode): ease in/out + dwell at the summit for a hero beat.
  function bodySpeedScale(t) {
    const edge = 0.10;
    let s = 1;
    if (t < edge) s = 0.45 + 0.55 * (t / edge);
    else if (t > 1 - edge) s = 0.45 + 0.55 * ((1 - t) / edge);
    const d = Math.abs(t - summitT), w = 0.07;
    if (d < w) s *= 0.45 + 0.55 * (d / w); // slow ~0.45x at the high point
    return Math.max(0.3, s);
  }

  let currentT = 0;
  let speedMultiplier = 1;
  let lastFrameTime = 0;
  let requestId = null;
  let started = false;
  let paused = false;
  let finished = false;
  let destroyed = false;
  let smoothedCenter = null; // eased camera centre; trails the head to round corners
  let smoothedBearing = null; // eased chase-cam heading (tracks travel direction)
  // Render-mode phase machine (headless capture): intro (2D->3D) -> body (flyover) -> outro (zoom-out).
  let renderPhase = "intro";
  let introElapsedMs = 0;
  let outroElapsedMs = 0;
  let outroFrom = null;
  let outroTo = null;
  let resolveFunc;
  const promise = new Promise(resolve => { resolveFunc = resolve; });

  // Cap how far the eased centre may trail the head, at a fraction of half the viewport's smaller
  // side. Easing assumes the head moves continuously, and it doesn't: it steps over the gaps between
  // a hike's routes in a single frame (see flyoverRoute.ts), and the centre then eased the WHOLE way
  // after it — which is the camera flying off to the start of the next route instead of a flyover.
  // Capped, the camera is at the new head within a frame and only the last screen-ish eases, which
  // is what the Android flyover does (HikePreviewCoordinator's cappedCentre).
  function centreCappedToHead(centre, headCoord, zoom) {
    const canvas = map.getCanvas ? map.getCanvas() : null;
    const halfExtentPx = canvas ? Math.min(canvas.clientWidth || 0, canvas.clientHeight || 0) / 2 : 0;
    if (!(halfExtentPx > 0)) return centre;
    const maxLagMeters = halfExtentPx * metersPerPixel(headCoord[1], zoom) * MAX_HEAD_OFFSET_FRACTION;
    return cappedCentre([centre[0], centre[1]], [headCoord[0], headCoord[1]], maxLagMeters);
  }

  // Render the camera + line + marker for a normalized progress t.
  // When animating we ease the camera centre toward the head (dtSeconds > 0); for discrete jumps
  // (intro hand-off, fast-forward/rewind) we snap the centre straight onto the head.
  function renderAt(t, snap = true, dtSeconds = 0) {
    const distKm = totalKm * t;
    // Never inside a gap: at a stroke's end the marker steps straight to the next stroke's start.
    const head = coordAtKm(route, distKm) || startCoord;

    let camCenter, camBearing, camZoom, camPitch;
    if (chaseCam) {
      // Heading = direction of travel from a short look-ahead window, eased to round off vertices.
      // Clamped to the current stroke: looking into the next one reads the bearing off the gap.
      const aheadPt = lookAheadCoordAtKm(route, distKm, CHASE_BEARING_LOOKAHEAD_KM) || head;
      const targetBearing = turf.distance(turf.point(head), turf.point(aheadPt), { units: "kilometers" }) > 1e-6
        ? turf.bearing(turf.point(head), turf.point(aheadPt))
        : (smoothedBearing != null ? smoothedBearing : initialTravelBearing);
      if (snap || smoothedBearing == null) {
        smoothedBearing = targetBearing;
      } else {
        smoothedBearing = lerpAngleShortest(smoothedBearing, targetBearing, exponentialSmoothingAlpha(dtSeconds, CHASE_BEARING_SMOOTH_TAU));
      }
      // Centre on the head (lightly eased to round corners) so the puck stays framed near centre.
      if (snap || !smoothedCenter) {
        smoothedCenter = [head[0], head[1]];
      } else {
        smoothedCenter = centreCappedToHead(
          smoothLngLat(smoothedCenter, head, exponentialSmoothingAlpha(dtSeconds, CENTER_SMOOTH_TAU)),
          head,
          CHASE_ZOOM
        );
      }
      camCenter = smoothedCenter; camBearing = smoothedBearing; camZoom = CHASE_ZOOM; camPitch = CHASE_PITCH;
    } else {
      camBearing = routeBearing + t * ORBIT_DEGREES;
      if (snap || !smoothedCenter) {
        smoothedCenter = [head[0], head[1]];
      } else {
        smoothedCenter = centreCappedToHead(
          smoothLngLat(smoothedCenter, head, exponentialSmoothingAlpha(dtSeconds, CENTER_SMOOTH_TAU)),
          head,
          FLYOVER_ZOOM
        );
      }
      camCenter = smoothedCenter; camZoom = FLYOVER_ZOOM; camPitch = FLYOVER_PITCH;
    }

    map.jumpTo({ center: camCenter, zoom: camZoom, bearing: camBearing, pitch: camPitch });
    setHead(head);

    const src = map.getSource("progressTrail");
    if (src) {
      // MultiLineString, one line per stroke: the pen lifts across a gap instead of drawing a
      // straight teleport over ground the hike never covers.
      const drawn = distKm <= 0.0005 ? [] : drawnStrokesUpToKm(route, distKm);
      src.setData(
        drawn.length > 0
          ? turf.multiLineString(drawn)
          : turf.lineString([startCoord, startCoord])
      );
    }

    if (progressCallback) {
      progressCallback(distKm, getElevationAtDistanceKm(distKm, coords3d, cumulativeDistKm));
    }
  }

  function frame() {
    if (paused || finished) { requestId = null; return; }
    const now = performance.now();
    const elapsedMs = now - lastFrameTime;
    lastFrameTime = now;
    currentT = advanceProgress(currentT, elapsedMs, duration, speedMultiplier);
    renderAt(currentT, false, Math.min(elapsedMs / 1000, MAX_FRAME_SECONDS));
    if (currentT < 1) {
      requestId = requestAnimationFrame(frame);
    } else {
      finished = true;
      playOutro();
      resolveFunc();
    }
  }

  function startFlyover() {
    if (started || destroyed) return;
    started = true;
    lastFrameTime = performance.now();
    requestId = requestAnimationFrame(frame);
  }

  // Flat 2D top-down (north) -> tilt up into the 3D flyover orientation, then start the flyover.
  function playIntro() {
    map.jumpTo({ center: startCoord, zoom: FLYOVER_ZOOM, bearing: 0, pitch: 0 });
    map.easeTo({ center: startCoord, zoom: FLYOVER_ZOOM, bearing: routeBearing, pitch: FLYOVER_PITCH, duration: INTRO_MS });
    // Timer-based handoff (robust regardless of moveend firing).
    setTimeout(() => startFlyover(), INTRO_MS + 50);
  }

  // On completion: smoothly zoom out to a top-down 2D view of the whole route.
  function playOutro() {
    const bounds = new mapboxgl.LngLatBounds();
    coords3d.forEach(c => bounds.extend([c[0], c[1]]));
    const cam = map.cameraForBounds(bounds, { padding: 60, bearing: 0 });
    if (cam) {
      map.easeTo({ center: cam.center, zoom: cam.zoom, bearing: 0, pitch: 0, duration: OUTRO_MS });
    }
  }

  // Deterministic counterparts of playIntro/playOutro for headless rendering: same start/end camera
  // poses, but stepped frame-by-frame (eased) instead of handed to Mapbox's wall-clock easeTo.
  function renderIntroFrame(p) {
    // End the intro at the body's opening pose so the hand-off doesn't jump: chase = travel-heading +
    // high pitch + closer zoom; orbit = route bearing + 45° pitch.
    const to = chaseCam
      ? { center: [startCoord[0], startCoord[1]] as [number, number], zoom: CHASE_ZOOM, bearing: initialTravelBearing, pitch: CHASE_PITCH }
      : { center: [startCoord[0], startCoord[1]] as [number, number], zoom: FLYOVER_ZOOM, bearing: routeBearing, pitch: FLYOVER_PITCH };
    // Reveal: open tight on the trailhead (zoomed in) and pull back to the flyover framing.
    const cam = interpolateCamera(
      { center: [startCoord[0], startCoord[1]], zoom: to.zoom + 1.8, bearing: 0, pitch: 0 },
      to,
      easeInOutCubic(p)
    );
    map.jumpTo(cam);
    setHead(startCoord);
  }

  function computeOutroTarget() {
    outroFrom = {
      center: smoothedCenter ? [smoothedCenter[0], smoothedCenter[1]] : [startCoord[0], startCoord[1]],
      zoom: chaseCam ? CHASE_ZOOM : FLYOVER_ZOOM,
      bearing: chaseCam ? (smoothedBearing != null ? smoothedBearing : initialTravelBearing) : routeBearing + ORBIT_DEGREES,
      pitch: chaseCam ? CHASE_PITCH : FLYOVER_PITCH
    };
    const bounds = new mapboxgl.LngLatBounds();
    coords3d.forEach(c => bounds.extend([c[0], c[1]]));
    const cam = map.cameraForBounds(bounds, { padding: 60, bearing: 0 });
    const center = cam
      ? (Array.isArray(cam.center) ? [cam.center[0], cam.center[1]] : [cam.center.lng, cam.center.lat])
      : outroFrom.center;
    outroTo = { center, zoom: cam ? cam.zoom : FLYOVER_ZOOM, bearing: 0, pitch: 0 };
  }

  function renderOutroFrame(p) {
    if (!outroFrom || !outroTo) computeOutroTarget();
    map.jumpTo(interpolateCamera(outroFrom, outroTo, easeInOutCubic(p)));
    // marker + fully-drawn progress line stay as the body left them
  }

  if (!renderMode) {
    playIntro();
  }

  return {
    pause() {
      paused = true;
      if (requestId) { cancelAnimationFrame(requestId); requestId = null; }
    },
    resume() {
      if (finished || !paused) return;
      paused = false;
      lastFrameTime = performance.now();
      if (started && !requestId) requestId = requestAnimationFrame(frame);
    },
    stop() {
      if (finished) return;
      finished = true;
      paused = true;
      if (requestId) { cancelAnimationFrame(requestId); requestId = null; }
      resolveFunc();
    },
    isPaused() { return paused; },
    fastForward() {
      currentT = Math.min(currentT + (totalKm > 0 ? 1 / totalKm : 0.05), 1);
      renderAt(currentT);
    },
    rewind() {
      currentT = Math.max(currentT - (totalKm > 0 ? 1 / totalKm : 0.05), 0);
      renderAt(currentT);
    },
    getProgress() { return currentT; },
    getEndState() { return { bearing: routeBearing, pitch: FLYOVER_PITCH }; },
    getStartState() { return { bearing: routeBearing, pitch: FLYOVER_PITCH }; },
    setSpeedMultiplier(nextSpeed) {
      if (typeof nextSpeed === "number" && nextSpeed > 0) speedMultiplier = nextSpeed;
    },
    getSpeedMultiplier() { return speedMultiplier; },

    // ---- Deterministic rendering (headless video capture) ----
    // Flyover body duration (ms) at 1x.
    getDurationMs() { return duration; },
    // Full clip duration (ms): intro + body + outro. Lets the renderer estimate the frame count.
    getTotalDurationMs() { return INTRO_MS + duration + OUTRO_MS; },
    // Reset the phase machine and render the flat 2D opening (intro at p=0). Call once before stepping.
    prepareForRender() {
      currentT = 0;
      smoothedCenter = null;
      started = true;
      paused = false;
      finished = false;
      renderPhase = "intro";
      introElapsedMs = 0;
      outroElapsedMs = 0;
      outroFrom = null;
      outroTo = null;
      renderIntroFrame(0);
    },
    // Advance the WHOLE clip by a fixed time step and render that frame, walking intro -> body ->
    // outro. The caller supplies dtSeconds (e.g. 1/30 for 30 fps) so output is reproducible.
    // Returns { done, phase, t } — done is true once the outro completes.
    renderNextFrame(dtSeconds) {
      const dtMs = dtSeconds * 1000;
      if (renderPhase === "intro") {
        introElapsedMs += dtMs;
        const p = Math.min(introElapsedMs / INTRO_MS, 1);
        renderIntroFrame(p);
        if (p >= 1) renderPhase = "body";
        return { done: false, phase: "intro", t: p };
      }
      if (renderPhase === "body") {
        currentT = advanceProgress(currentT, dtMs * bodySpeedScale(currentT), duration, speedMultiplier);
        renderAt(currentT, false, dtSeconds);
        if (currentT >= 1) { renderPhase = "outro"; outroElapsedMs = 0; computeOutroTarget(); }
        return { done: false, phase: "body", t: currentT };
      }
      if (renderPhase === "outro") {
        outroElapsedMs += dtMs;
        const p = Math.min(outroElapsedMs / OUTRO_MS, 1);
        renderOutroFrame(p);
        if (p >= 1) { renderPhase = "done"; finished = true; }
        return { done: renderPhase === "done", phase: "outro", t: p };
      }
      return { done: true, phase: "done", t: 1 };
    },
    // Body-only stepping (no intro/outro); kept for callers that want just the flyover.
    renderFlyoverFrame(dtSeconds) {
      if (finished) return { done: true, t: 1 };
      currentT = advanceProgress(currentT, dtSeconds * 1000, duration, speedMultiplier);
      renderAt(currentT, false, dtSeconds);
      if (currentT >= 1) finished = true;
      return { done: finished, t: currentT };
    },

    then(onFulfilled, onRejected) { return promise.then(onFulfilled, onRejected); },
    destroy() {
      if (destroyed) return;
      destroyed = true;
      paused = true;
      finished = true;
      if (requestId) { cancelAnimationFrame(requestId); requestId = null; }
      if (map.getLayer("flyoverHead")) map.removeLayer("flyoverHead");
      if (map.getLayer("flyoverHeadBorder")) map.removeLayer("flyoverHeadBorder");
      if (map.getSource("flyoverHead")) map.removeSource("flyoverHead");
      resolveFunc();
    }
  };
}

// ----------------------------- Helpers -----------------------------

